// hooks/useImagePlayer.ts - SUBSTITUI useVideoPlayer
import { useCallback, useEffect, useRef, useState } from 'react';
import { ProjectImage } from 'services/videoService';

interface UseImagePlayerOptions {
  images: ProjectImage[];
  initialIndex?: number;
}

export const useImagePlayer = ({
  images,
  initialIndex = 0
}: UseImagePlayerOptions) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 5x, etc.

  const playIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Garantir que o índice está dentro dos limites
  const currentIndex = Math.max(
    0,
    Math.min(currentImageIndex, images.length - 1)
  );
  const currentImage = images[currentIndex];

  console.log('[useImagePlayer] 🎬 Estado:', {
    currentIndex,
    total: images.length,
    playing: isPlaying,
    speed: playbackSpeed + 'x'
  });

  // Auto-play effect
  useEffect(() => {
    if (isPlaying && images.length > 0) {
      console.log(
        `[useImagePlayer] ▶️ Play iniciado (velocidade: ${playbackSpeed}x)`
      );

      // Calcular intervalo baseado na velocidade
      // 1x = 1000ms (1 FPS), 2x = 500ms, 5x = 200ms, etc.
      const interval = 1000 / playbackSpeed;

      playIntervalRef.current = setInterval(() => {
        setCurrentImageIndex(prev => {
          if (prev >= images.length - 1) {
            // Chegou ao final, parar
            console.log('[useImagePlayer] 🏁 Fim do slideshow');
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, interval);
    } else {
      console.log('[useImagePlayer] ⏸️ Play pausado');
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
        playIntervalRef.current = null;
      }
    }

    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, images.length]);

  // Handler: Play
  const handlePlay = useCallback(() => {
    console.log('[useImagePlayer] ▶️ Play');
    setIsPlaying(true);
  }, []);

  // Handler: Pause
  const handlePause = useCallback(() => {
    console.log('[useImagePlayer] ⏸️ Pause');
    setIsPlaying(false);
  }, []);

  // Handler: Toggle Play/Pause
  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Handler: Mudar velocidade
  const changePlaybackSpeed = useCallback((speed: number) => {
    console.log('[useImagePlayer] ⚡ Mudando velocidade:', speed + 'x');
    setPlaybackSpeed(speed);
  }, []);

  // Handler: Navegar frames
  const navigateFrames = useCallback(
    (count: number) => {
      const newIndex = Math.max(
        0,
        Math.min(currentIndex + count, images.length - 1)
      );

      console.log(`[useImagePlayer] 🎬 Navegando ${count} frames:`, {
        de: currentIndex,
        para: newIndex
      });

      setCurrentImageIndex(newIndex);

      // Se estava tocando, pausar para navegação manual
      if (isPlaying) {
        setIsPlaying(false);
      }
    },
    [currentIndex, images.length, isPlaying]
  );

  // Handler: Ir para frame específico
  const goToFrame = useCallback(
    (frameNumber: number) => {
      const newIndex = Math.max(0, Math.min(frameNumber, images.length - 1));

      console.log(`[useImagePlayer] 🎯 Indo para frame ${frameNumber}:`, {
        atual: currentIndex,
        novo: newIndex
      });

      setCurrentImageIndex(newIndex);

      // Se estava tocando, pausar
      if (isPlaying) {
        setIsPlaying(false);
      }
    },
    [currentIndex, images.length, isPlaying]
  );

  // Handler: Mudar imagem manualmente
  const handleImageChange = useCallback(
    (indexOrCallback: number | ((prev: number) => number)) => {
      if (typeof indexOrCallback === 'function') {
        setCurrentImageIndex(prev => {
          const newIndex = indexOrCallback(prev);
          return Math.max(0, Math.min(newIndex, images.length - 1));
        });
      } else {
        const newIndex = Math.max(
          0,
          Math.min(indexOrCallback, images.length - 1)
        );
        setCurrentImageIndex(newIndex);
      }
    },
    [images.length]
  );

  // Obter informações do frame
  const getFrameInfo = useCallback(() => {
    return {
      currentFrame: currentIndex,
      totalFrames: images.length,
      currentImage: currentImage,
      progress:
        images.length > 0 ? (currentIndex / (images.length - 1)) * 100 : 0
    };
  }, [currentIndex, images.length, currentImage]);

  return {
    // Estado
    currentImageIndex: currentIndex,
    currentImage,
    isPlaying,
    playbackSpeed,

    // Handlers
    handlePlay,
    handlePause,
    togglePlayPause,
    changePlaybackSpeed,
    navigateFrames,
    goToFrame,
    handleImageChange,

    // Helpers
    getFrameInfo,

    // Setters diretos (se necessário)
    setCurrentImageIndex,
    setIsPlaying
  };
};
